import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";

import Loader from "../../components/Loader/Loader";
import AnalyzeBriefLoader from "../components/AnalyzeBriefLoader";

import { useTableSearch, TableSearchBar } from "src/app/components/TableSearch";
import { getCreatorCellValues } from "./utils/getCreatorCellValues";
import CreatorResultsTable from "./CreatorResultsTable";

import "./CreatorResults.scss";

export default function CreatorResults(props) {
  const {
    searchData,
    getListData,
    userList,
    getMoreResults,
    listLoading,
    toggleFilters,
    endOfList,
    getScrollParent = () => null,
    labelsHeight = 0,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
  } = props;

  const audienceLoading = useSelector(
    (state) => state.creatorSearchReducer.audienceLoading,
  );
  const isAnalyzingBrief = useSelector(
    (state) => state.creatorSearchReducer.isAnalyzingBrief,
  );
  const resultsColumns = useSelector(
    (state) => state.creatorSearchReducer.columns,
  );

  const scrollParentRef = useRef(null);
  const previousListLengthRef = useRef(0);
  const isLoadingMoreRef = useRef(false);

  const cellTextRows = useMemo(
    () => getCreatorCellValues(userList, resultsColumns),
    [userList, resultsColumns],
  );

  // Track list length changes and set flag to block scroll during InfiniteScroll loading
  useEffect(() => {
    if (userList.length > previousListLengthRef.current) {
      // List has grown - InfiniteScroll loaded new results
      isLoadingMoreRef.current = true;
      previousListLengthRef.current = userList.length;

      // Unblock scroll after a short delay (allow new data to be processed)
      const timer = setTimeout(() => {
        isLoadingMoreRef.current = false;
      }, 300);

      return () => clearTimeout(timer);
    } else if (userList.length < previousListLengthRef.current) {
      // List has shrunk - e.g., filters changed
      previousListLengthRef.current = userList.length;
      isLoadingMoreRef.current = false;
    }
  }, [userList.length]);

  const handleScrollToResult = useCallback(
    (result) => {
      const { rowIndex } = result;
      if (typeof rowIndex !== "number") return;

      // Don't scroll if new results were just loaded by InfiniteScroll
      if (isLoadingMoreRef.current) {
        return;
      }

      const scrollParent = getScrollParent() || scrollParentRef.current;
      if (!scrollParent) return;

      setTimeout(() => {
        const rows = scrollParent.querySelectorAll(".cdt-table__row");
        const targetRow = rows[rowIndex];
        if (targetRow) {
          targetRow.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
    },
    [getScrollParent],
  );

  const tableSearch = useTableSearch({
    cellTextRows,
    onScrollToResult: handleScrollToResult,
  });

  if (isAnalyzingBrief) {
    return <AnalyzeBriefLoader />;
  }

  if (audienceLoading && !listLoading) {
    return <Loader />;
  }
  return (
    <div className="audience-results creator-results">
      <TableSearchBar search={tableSearch} />
      <CreatorResultsTable
        userList={userList}
        endOfList={endOfList}
        labelsHeight={labelsHeight}
        searchData={searchData}
        getListData={getListData}
        getMoreResults={getMoreResults}
        listLoading={listLoading}
        getScrollParent={getScrollParent}
        toggleFilters={toggleFilters}
        tableSearch={tableSearch}
        sortBy={sortBy}
        setSortBy={setSortBy}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
      />
    </div>
  );
}
